import { useReadOnly } from '@/lib/readOnly';
import { useEffect, useState, type ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cx } from '@/lib/cx';
import { describeError } from '@/lib/api';
import { Button } from './Button';
import { Field, Textarea } from './Field';
import { InlineAlert } from './Misc';

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** Prevent closing via overlay click/Escape while a request is in flight. */
  dismissible?: boolean;
  className?: string;
}

/**
 * Application modal. Focus is trapped and restored to the trigger on close (Radix).
 * Use for short decisions and focused forms; use Drawer for longer create/edit flows.
 */
export function Modal({ open, onOpenChange, title, description, children, footer, size = 'md', dismissible = true, className }: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={(o) => (dismissible || o ? onOpenChange(o) : undefined)}>
      <Dialog.Portal>
        <Dialog.Overlay className="overlay" />
        <Dialog.Content
          className={cx('modal', `modal-${size}`, className)}
          onEscapeKeyDown={(e) => !dismissible && e.preventDefault()}
          onPointerDownOutside={(e) => !dismissible && e.preventDefault()}
          aria-describedby={description ? undefined : undefined}
        >
          <header className="modal-head">
            <div className="modal-head-text">
              <Dialog.Title className="modal-title">{title}</Dialog.Title>
              {description ? <Dialog.Description className="modal-desc">{description}</Dialog.Description> : <Dialog.Description className="sr-only">Dialog</Dialog.Description>}
            </div>
            <Dialog.Close asChild>
              <button type="button" className="modal-close" aria-label="Close" disabled={!dismissible}>
                <X size={16} />
              </button>
            </Dialog.Close>
          </header>
          {children && <div className="modal-body">{children}</div>}
          {footer && <footer className="modal-foot">{footer}</footer>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: 'danger' | 'primary';
  /** Collect a mandatory reason (e.g. cancel / put on hold). */
  reason?: { label: string; help?: string; minLength?: number; placeholder?: string };
  onConfirm: (reason?: string) => Promise<unknown> | unknown;
  confirmDisabled?: boolean;
}

/** Confirmation for consequential or destructive actions. Never use window.confirm. */
export function ConfirmDialog({ open, onOpenChange, title, description, children, confirmLabel, cancelLabel = 'Cancel', tone = 'danger', reason, onConfirm, confirmDisabled }: ConfirmDialogProps) {
  const readOnly = useReadOnly();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [text, setText] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setError(null);
      setText('');
      setTouched(false);
    }
  }, [open]);

  const min = reason?.minLength ?? 3;
  const reasonInvalid = !!reason && text.trim().length < min;

  const submit = async () => {
    setTouched(true);
    if (reasonInvalid || readOnly) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(reason ? text.trim() : undefined);
      onOpenChange(false);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const err = error ? describeError(error) : null;
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      size="sm"
      dismissible={!busy}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} onClick={submit} loading={busy} disabled={confirmDisabled || readOnly}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <form
        className="stack"
        style={{ ['--stack-gap' as string]: 'var(--space-3)' }}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        {children}
        {reason && (
          <Field label={reason.label} help={reason.help} required error={touched && reasonInvalid ? `Enter at least ${min} characters.` : null} counter={{ value: text.length, max: 1000 }}>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={1000} placeholder={reason.placeholder} autoFocus />
          </Field>
        )}
        {err && (
          <InlineAlert tone="danger" title={err.title}>
            {err.message}
          </InlineAlert>
        )}
      </form>
    </Modal>
  );
}
